import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientFromFirebase } from "../../../redux/slice/clientConfiguration";
import { fetchCredentialConfig } from "../../../redux/slice/credentialConfigSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { groupTabCredentialsByPortalId } from "../utils/groupCredentialsByPortalId";

/**
 * Loads credentials + client lists from Redux on mount and derives the
 * data shapes the credentials configuration page renders.
 *
 * Keeps console logs in place so debugging output behaves the same as before.
 */
export const useCredentialsData = (selectedClientId: string | null) => {
  const dispatch = useDispatch<AppDispatch>();

  const { credentials, isLoading } = useSelector(
    (state: RootState) => state.credentialsConfig,
  );

  const { data: clientName, loading: isLoadingClient } = useSelector(
    (state: RootState) => state.clientConfiguration,
  );

  useEffect(() => {
    dispatch(fetchCredentialConfig());
    dispatch(fetchClientFromFirebase());
  }, [dispatch]);

  const clientOptions = useMemo(
    () =>
      clientName.map((client) => ({
        value: client.DocId ?? "",
        label: client.name ?? "",
      })),
    [clientName],
  );

  const filteredCredentials = useMemo(
    () =>
      credentials?.filter((credential) => {
        if (!selectedClientId) return false;
        return credential.client_id === selectedClientId;
      }) || [],
    [credentials, selectedClientId],
  );

  /**
   * Builds the three top-level tab buckets shown on the credentials page.
   *
   * Bucketing rules:
   * 1. Split by `is_queue` first.
   *    - Queue credentials (`is_queue === true`) are further split into MFA
   *      and OTP by the presence of `secret_key`.
   *    - Non-queue credentials (`is_queue !== true`, which includes both
   *      `false` and missing/undefined) go into a single bucket.
   *
   * Tab order is fixed (MFA, OTP, Non-Queue) so the default `selectedTab`
   * in the page (`"MFA"`) keeps working without changes.
   */
  const groupedCredentials = useMemo(() => {
    const queueCredentials = filteredCredentials.filter(
      (credential) => credential.is_queue === true,
    );
    const nonQueueCredentials = filteredCredentials.filter(
      (credential) => credential.is_queue !== true,
    );

    return [
      {
        label: "Portals with MFA",
        credentials: queueCredentials.filter(
          (credential) => !!credential.secret_key,
        ),
        value: "MFA",
      },
      {
        label: "Portals with OTP",
        credentials: queueCredentials.filter(
          (credential) => !credential.secret_key,
        ),
        value: "OTP",
      },
      {
        label: "Non-Queue Portals",
        credentials: nonQueueCredentials,
        value: "NON_QUEUE",
      },
    ];
  }, [filteredCredentials]);

  const groupedCredentialsByPortal = useMemo(
    () => groupTabCredentialsByPortalId(groupedCredentials),
    [groupedCredentials],
  );


  return {
    credentials,
    isLoading,
    clientName,
    isLoadingClient,
    clientOptions,
    filteredCredentials,
    groupedCredentials,
    groupedCredentialsByPortal,
  };
};
